import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Save, ShieldCheck, Printer, BookmarkPlus, Sparkles, Trash2 } from "lucide-react";
import { toast } from "sonner";
import MicDictateButton from "@/components/MicDictateButton";

const CHECKLIST = [
  { key: "consent_signed", label: "Consent signed" },
  { key: "fitness_uploaded", label: "Fitness clearance reviewed" },
  { key: "investigations_reviewed", label: "Investigations reviewed" },
  { key: "site_marked", label: "Surgical site marked" },
  { key: "nbm_confirmed", label: "NBM confirmed" },
  { key: "allergy_checked", label: "Allergy checked" },
  { key: "iol_confirmed", label: "IOL model & power confirmed" },
];

const WHO = [
  { key: "sign_in", label: "Sign-In (before induction)" },
  { key: "time_out", label: "Time-Out (before incision)" },
  { key: "sign_out", label: "Sign-Out (before patient leaves OT)" },
];

// Surgery-based template categories (same vocabulary as IPD discharge for cross-consistency)
const SURGERY_CATEGORIES = [
  "Cataract / Phaco", "Cataract / SICS", "IOL Exchange",
  "Glaucoma / Trabeculectomy", "Glaucoma / Implant",
  "Vitreoretinal / Vitrectomy", "Vitreoretinal / Buckle",
  "Retina / Intravitreal Injection",
  "Cornea / Keratoplasty", "Cornea / Pterygium",
  "Oculoplasty / Lid Surgery", "Oculoplasty / DCR",
  "Squint / Strabismus",
  "Refractive / LASIK", "Refractive / PRK",
  "Other",
];

export default function OtNote() {
  const { id } = useParams();
  const [booking, setBooking] = useState(null);
  const [implants, setImplants] = useState([]);
  const [stockRows, setStockRows] = useState([]);
  const [note, setNote] = useState({
    procedure_performed: "", eye_operated: "OD", anesthesia_type: "topical",
    consumables_used: "", intra_op_notes: "", post_op_notes: "", post_op_instructions: "", complications: "",
    nurse_name: "", technician_name: "", anesthetist_name: "", ot_room: "", start_time: "", end_time: "",
    iol: { implant_id: "", implant_name: "", model: "", power: "", batch_no: "", lot_no: "", expiry: "", sticker_file_id: "", unit_cost: 0 },
    who_safety: { sign_in: false, time_out: false, sign_out: false },
    pre_op_checklist: Object.fromEntries(CHECKLIST.map(c => [c.key, false])),
  });
  const [savedOnce, setSavedOnce] = useState(false);
  // Templates
  const [templates, setTemplates] = useState([]);
  const [selectedTpl, setSelectedTpl] = useState("");
  const [tplOpen, setTplOpen] = useState(false);
  const [tplName, setTplName] = useState("");
  const [tplShared, setTplShared] = useState(false);
  const [tplCategory, setTplCategory] = useState("");
  const [tplFilterCategory, setTplFilterCategory] = useState("All");

  const loadTemplates = async () => {
    try {
      const { data } = await api.get("/ot/note-templates");
      setTemplates(data.items || []);
    } catch (_e) { /* templates are optional */ }
  };
  useEffect(() => { loadTemplates(); }, []);

  const applyTemplate = (tplId) => {
    setSelectedTpl(tplId);
    if (!tplId) return;
    const tpl = templates.find(t => t.id === tplId);
    if (!tpl) return;
    const t = tpl.template || {};
    // Merge only the narrative fields; preserve patient/IOL/checklist context.
    setNote((prev) => ({
      ...prev,
      procedure_performed: t.procedure_performed ?? prev.procedure_performed,
      eye_operated: t.eye_operated ?? prev.eye_operated,
      anesthesia_type: t.anesthesia_type ?? prev.anesthesia_type,
      consumables_used: t.consumables_used ?? prev.consumables_used,
      intra_op_notes: t.intra_op_notes ?? prev.intra_op_notes,
      post_op_notes: t.post_op_notes ?? prev.post_op_notes,
      post_op_instructions: t.post_op_instructions ?? prev.post_op_instructions,
      complications: t.complications ?? prev.complications,
    }));
    toast.success(`Template "${tpl.name}" applied`);
  };

  const saveAsTemplate = async () => {
    if (!tplName.trim()) { toast.error("Template name is required"); return; }
    try {
      const snapshot = {
        procedure_performed: note.procedure_performed,
        eye_operated: note.eye_operated,
        anesthesia_type: note.anesthesia_type,
        consumables_used: note.consumables_used,
        intra_op_notes: note.intra_op_notes,
        post_op_notes: note.post_op_notes,
        post_op_instructions: note.post_op_instructions,
        complications: note.complications,
      };
      await api.post("/ot/note-templates", { name: tplName.trim(), category: tplCategory.trim(), is_shared: tplShared, template: snapshot });
      toast.success("Template saved");
      setTplOpen(false); setTplName(""); setTplShared(false); setTplCategory("");
      loadTemplates();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  const deleteTemplate = async (tplId, name) => {
    if (!window.confirm(`Delete template "${name}"?`)) return;
    try {
      await api.delete(`/ot/note-templates/${tplId}`);
      toast.success("Template deleted");
      setSelectedTpl("");
      loadTemplates();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  useEffect(() => {
    (async () => {
      const [bk, im, st] = await Promise.all([
        api.get(`/ot/bookings/${id}`),
        api.get(`/ot/implants?active=true`).catch(() => ({ data: { items: [] } })),
        api.get(`/ot/implants/stock`).catch(() => ({ data: { items: [] } })),
      ]);
      setBooking(bk.data.booking);
      if (bk.data.note) { setNote((prev) => ({ ...prev, ...bk.data.note })); setSavedOnce(true); }
      setImplants(im.data.items || []);
      setStockRows(st.data.items || []);
      // eslint-disable-next-line
    })();
  }, [id]);

  // Batch options for the selected implant (only batches with on-hand > 0, sorted nearest-expiry first)
  const batchesForImplant = (implantId) => stockRows
    .filter((r) => r.implant_id === implantId && r.on_hand > 0 && r.status !== "expired")
    .sort((a, b) => (a.expiry || "9999").localeCompare(b.expiry || "9999"));

  const pickImplant = (implantId) => {
    const im = implants.find((i) => i.id === implantId);
    if (!im) return;
    const batches = batchesForImplant(implantId);
    const b = batches[0];  // auto-pick nearest-expiry batch
    setNote((prev) => ({
      ...prev,
      iol: {
        ...prev.iol,
        implant_id: implantId,
        implant_name: im.name,
        model: im.model || prev.iol.model || "",
        power: im.default_power || prev.iol.power || "",
        batch_no: b?.batch_no || "",
        lot_no: b?.lot_no || "",
        expiry: b?.expiry || "",
        unit_cost: b?.unit_cost || 0,
      },
    }));
  };

  const pickBatch = (batchNo) => {
    const b = batchesForImplant(note.iol.implant_id).find((r) => r.batch_no === batchNo);
    if (!b) return;
    setNote((prev) => ({ ...prev, iol: { ...prev.iol, batch_no: b.batch_no, lot_no: b.lot_no, expiry: b.expiry, unit_cost: b.unit_cost || 0 } }));
  };

  const save = async () => {
    try {
      await api.post(`/ot/bookings/${id}/note`, note);
      setSavedOnce(true);
      toast.success("Surgery note saved · booking marked completed", {
        description: "You can now print the operative record (NABH).",
        action: { label: "Print", onClick: () => window.open(`/print/ot/${id}`, "_blank") },
        duration: 6000,
      });
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  if (!booking) return <div className="text-sm text-ink-muted">Loading…</div>;

  return (
    <div className="space-y-6 animate-fade-in pb-24">
      <div className="flex items-end justify-between">
        <div>
          <div className="section-label">OT · NABH + WHO surgical safety</div>
          <h1 className="font-heading text-3xl font-semibold">{booking.surgery_name}</h1>
          <div className="mt-1 text-sm text-ink-muted">{booking.patient_name} · <span className="font-mono text-xs">{booking.patient_uhid}</span> · Eye: <b>{booking.eye}</b> · {booking.scheduled_date}</div>
        </div>
        {savedOnce && (
          <Link to={`/print/ot/${id}`} target="_blank">
            <Button variant="outline" className="border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100" data-testid="ot-print-btn"><Printer size={14} className="mr-2" />Print operative record</Button>
          </Link>
        )}
      </div>

      <Card className="p-3" data-testid="ot-template-bar">
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex-1 min-w-[220px]">
            <Label className="text-[11px] uppercase tracking-wider text-violet-700 mb-1 flex items-center gap-1.5">
              <Sparkles size={12} />Apply a saved OT note template
            </Label>
            <select
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
              value={selectedTpl}
              onChange={(e) => applyTemplate(e.target.value)}
              data-testid="ot-template-select"
            >
              <option value="">— Select a template —</option>
              {templates
                .filter((t) => tplFilterCategory === "All" || (t.category || "Other") === tplFilterCategory)
                .map((t) => (
                  <option key={t.id} value={t.id}>{t.name}{t.category ? ` · ${t.category}` : ""}{t.is_shared ? " · shared" : ""}</option>
                ))}
            </select>
          </div>
          {selectedTpl && (
            <Button
              variant="outline" size="sm"
              className="border-rose-200 text-rose-600 hover:bg-rose-50"
              onClick={() => deleteTemplate(selectedTpl, templates.find(t => t.id === selectedTpl)?.name)}
              data-testid="ot-template-delete"
              title="Delete selected template"
            >
              <Trash2 size={14} />
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={() => setTplOpen(true)} data-testid="ot-save-template-btn">
            <BookmarkPlus size={12} className="mr-1" />Save as template
          </Button>
          <div className="text-[11px] text-ink-muted">
            {templates.length === 0 ? "No templates yet — fill the form & click Save as template." : `${templates.length} template${templates.length === 1 ? "" : "s"} available`}
          </div>
        </div>
        {templates.length > 0 && (
          <div className="mt-2.5 flex flex-wrap gap-1.5" data-testid="ot-template-category-filter">
            {["All", ...SURGERY_CATEGORIES].map((cat) => {
              const count = cat === "All" ? templates.length : templates.filter((t) => (t.category || "Other") === cat).length;
              if (cat !== "All" && count === 0) return null;
              return (
                <button
                  key={cat}
                  type="button"
                  onClick={() => setTplFilterCategory(cat)}
                  className={`rounded-full border px-2.5 py-0.5 text-[10px] font-medium transition ${tplFilterCategory === cat ? "border-violet-600 bg-violet-600 text-white" : "border-violet-200 bg-white text-ink hover:border-violet-600"}`}
                  data-testid={`ot-tpl-cat-${cat.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
                >
                  {cat} <span className="opacity-70">· {count}</span>
                </button>
              );
            })}
          </div>
        )}
      </Card>

      <Card className="p-5">
        <div className="section-label mb-3">Pre-op checklist (NABH)</div>
        <div className="grid gap-2 md:grid-cols-2">
          {CHECKLIST.map(c => (
            <label key={c.key} className="flex items-center gap-2 rounded-md border border-border p-2 text-sm">
              <Checkbox checked={!!note.pre_op_checklist[c.key]} onCheckedChange={(v) => setNote({...note, pre_op_checklist: {...note.pre_op_checklist, [c.key]: !!v}})} data-testid={`ot-checklist-${c.key}`} />
              {c.label}
            </label>
          ))}
        </div>
      </Card>

      <Card className="p-5">
        <div className="section-label mb-3">WHO Surgical Safety Checklist</div>
        <div className="grid gap-2 md:grid-cols-3">
          {WHO.map(w => (
            <label key={w.key} className="flex items-center gap-2 rounded-md border border-border p-2 text-sm">
              <Checkbox checked={!!note.who_safety[w.key]} onCheckedChange={(v) => setNote({...note, who_safety: {...note.who_safety, [w.key]: !!v}})} data-testid={`ot-who-${w.key}`} />
              {w.label}
            </label>
          ))}
        </div>
      </Card>

      <Card className="p-5">
        <div className="section-label mb-3">Surgery details</div>
        <div className="form-grid">
          <div><div className="flex items-center justify-between"><Label>Procedure performed</Label><MicDictateButton testId="mic-ot-procedure" value={note.procedure_performed} onChange={(v) => setNote({...note, procedure_performed: v})} compact /></div><Input value={note.procedure_performed} onChange={e => setNote({...note, procedure_performed: e.target.value})} data-testid="ot-procedure" /></div>
          <div>
            <Label>Eye operated</Label>
            <Select value={note.eye_operated} onValueChange={v => setNote({...note, eye_operated: v})}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="OD">OD</SelectItem><SelectItem value="OS">OS</SelectItem><SelectItem value="OU">OU</SelectItem></SelectContent>
            </Select>
          </div>
          <div>
            <Label>Anesthesia</Label>
            <Select value={note.anesthesia_type} onValueChange={v => setNote({...note, anesthesia_type: v})}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{["topical","peribulbar","retrobulbar","sub-tenon","general"].map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="md:col-span-3"><div className="flex items-center justify-between"><Label>Consumables used</Label><MicDictateButton testId="mic-ot-consumables" value={note.consumables_used} onChange={(v) => setNote({...note, consumables_used: v})} compact /></div><Input value={note.consumables_used} onChange={e => setNote({...note, consumables_used: e.target.value})} /></div>
          <div className="md:col-span-3"><div className="flex items-center justify-between"><Label>Intra-op notes</Label><MicDictateButton testId="mic-ot-intra-op" value={note.intra_op_notes} onChange={(v) => setNote({...note, intra_op_notes: v})} compact /></div><Textarea rows={2} value={note.intra_op_notes} onChange={e => setNote({...note, intra_op_notes: e.target.value})} /></div>
          <div className="md:col-span-3"><div className="flex items-center justify-between"><Label>Post-op notes</Label><MicDictateButton testId="mic-ot-post-op" value={note.post_op_notes} onChange={(v) => setNote({...note, post_op_notes: v})} compact /></div><Textarea rows={2} value={note.post_op_notes} onChange={e => setNote({...note, post_op_notes: e.target.value})} /></div>
          <div className="md:col-span-3"><div className="flex items-center justify-between"><Label>Post-op instructions</Label><MicDictateButton testId="mic-ot-post-op-inst" value={note.post_op_instructions} onChange={(v) => setNote({...note, post_op_instructions: v})} compact /></div><Textarea rows={2} value={note.post_op_instructions} onChange={e => setNote({...note, post_op_instructions: e.target.value})} /></div>
          <div className="md:col-span-3"><div className="flex items-center justify-between"><Label>Complications</Label><MicDictateButton testId="mic-ot-complications" value={note.complications} onChange={(v) => setNote({...note, complications: v})} compact /></div><Input value={note.complications} onChange={e => setNote({...note, complications: e.target.value})} /></div>
        </div>
      </Card>

      <Card className="p-5">
        <div className="section-label mb-3">Operative team & timing</div>
        <div className="form-grid">
          <div><Label>OT room</Label><Input value={note.ot_room} onChange={e => setNote({...note, ot_room: e.target.value})} placeholder="e.g. OT-1" data-testid="ot-room" /></div>
          <div><Label>Start time</Label><Input type="time" value={note.start_time} onChange={e => setNote({...note, start_time: e.target.value})} /></div>
          <div><Label>End time</Label><Input type="time" value={note.end_time} onChange={e => setNote({...note, end_time: e.target.value})} /></div>
          <div><Label>OT nurse</Label><Input value={note.nurse_name} onChange={e => setNote({...note, nurse_name: e.target.value})} placeholder="Staff nurse" data-testid="ot-nurse" /></div>
          <div><Label>OT technician</Label><Input value={note.technician_name} onChange={e => setNote({...note, technician_name: e.target.value})} placeholder="Scrub technician" data-testid="ot-technician" /></div>
          <div><Label>Anaesthetist</Label><Input value={note.anesthetist_name} onChange={e => setNote({...note, anesthetist_name: e.target.value})} placeholder="Anaesthetist on duty" data-testid="ot-anesthetist" /></div>
        </div>
      </Card>

      <Card className="p-5">
        <div className="mb-3 flex items-center justify-between">
          <div>
            <div className="section-label">IOL / Implant details</div>
            <div className="text-[11px] text-ink-muted">Pick from your hospital catalogue or enter manually for off-catalogue/emergency use.</div>
          </div>
          <Link to="/app/ot/implants" className="text-[11px] font-semibold text-brand hover:underline" data-testid="ot-implant-master-link">Manage catalogue →</Link>
        </div>
        <div className="form-grid">
          <div className="md:col-span-2">
            <Label>Implant <span className="text-[10px] text-ink-muted">(from catalogue)</span></Label>
            <Select value={note.iol.implant_id || ""} onValueChange={(v) => pickImplant(v)}>
              <SelectTrigger data-testid="ot-iol-implant-picker"><SelectValue placeholder="Select an implant from the master catalogue…" /></SelectTrigger>
              <SelectContent>
                {implants.length === 0 && <div className="px-2 py-1 text-xs text-ink-muted">No implants in catalogue — go to Implants menu to add.</div>}
                {implants.map((i) => {
                  const onHand = stockRows.filter((r) => r.implant_id === i.id && r.on_hand > 0 && r.status !== "expired").reduce((s, r) => s + r.on_hand, 0);
                  return <SelectItem key={i.id} value={i.id}>{i.name}{i.model ? ` · ${i.model}` : ""} {onHand > 0 ? `· ${onHand} in stock` : "· ⚠ out of stock"}</SelectItem>;
                })}
              </SelectContent>
            </Select>
          </div>
          {note.iol.implant_id && (
            <div className="md:col-span-2">
              <Label>Batch <span className="text-[10px] text-ink-muted">(nearest expiry first)</span></Label>
              <Select value={note.iol.batch_no || ""} onValueChange={(v) => pickBatch(v)}>
                <SelectTrigger data-testid="ot-iol-batch-picker"><SelectValue placeholder="Select a batch…" /></SelectTrigger>
                <SelectContent>
                  {batchesForImplant(note.iol.implant_id).length === 0 && <div className="px-2 py-1 text-xs text-rose-700">No batches in stock for this implant.</div>}
                  {batchesForImplant(note.iol.implant_id).map((r) => (
                    <SelectItem key={r.batch_no} value={r.batch_no}>
                      {r.batch_no} · exp {r.expiry} · {r.on_hand} on hand
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div><Label>Model</Label><Input value={note.iol.model} onChange={e => setNote({...note, iol: {...note.iol, model: e.target.value}})} data-testid="ot-iol-model" /></div>
          <div><Label>Power</Label><Input value={note.iol.power} onChange={e => setNote({...note, iol: {...note.iol, power: e.target.value}})} /></div>
          <div><Label>Batch no.</Label><Input value={note.iol.batch_no} onChange={e => setNote({...note, iol: {...note.iol, batch_no: e.target.value, implant_id: note.iol.implant_id }})} data-testid="ot-iol-batch" /></div>
          <div><Label>Lot no.</Label><Input value={note.iol.lot_no} onChange={e => setNote({...note, iol: {...note.iol, lot_no: e.target.value}})} /></div>
          <div><Label>Expiry</Label><Input type="date" value={note.iol.expiry} onChange={e => setNote({...note, iol: {...note.iol, expiry: e.target.value}})} /></div>
          <div><Label>Unit cost (₹)</Label><Input type="number" value={note.iol.unit_cost || 0} onChange={e => setNote({...note, iol: {...note.iol, unit_cost: parseFloat(e.target.value) || 0}})} /></div>
        </div>
      </Card>

      <div className="sticky bottom-0 -mx-4 flex items-center justify-between border-t border-border bg-white px-4 py-3 shadow-[0_-2px_8px_-2px_rgba(0,0,0,0.05)] md:-mx-6 lg:-mx-8">
        <div className="text-xs text-ink-muted"><ShieldCheck size={11} className="mr-1 inline" />NABH + WHO compliant operative record · audit-logged</div>
        <Button onClick={save} className="bg-brand hover:bg-brand-700" data-testid="ot-save-note-btn"><Save size={14} className="mr-2" />Save surgery note</Button>
      </div>

      {/* Save-as-template dialog */}
      <Dialog open={tplOpen} onOpenChange={setTplOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading text-base flex items-center gap-2"><BookmarkPlus size={16} className="text-brand" />Save OT note as template</DialogTitle></DialogHeader>
          <div className="grid gap-3">
            <div>
              <Label>Template name *</Label>
              <Input value={tplName} onChange={(e) => setTplName(e.target.value)} placeholder="e.g. Phacoemulsification standard, Trab + MMC, Pterygium…" data-testid="ot-template-name" />
            </div>
            <div>
              <Label>Surgery category</Label>
              <select
                className="mt-1 w-full rounded-md border bg-background px-3 py-2 text-sm"
                value={tplCategory}
                onChange={(e) => setTplCategory(e.target.value)}
                data-testid="ot-template-category"
              >
                <option value="">— Uncategorised —</option>
                {SURGERY_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <div className="mt-1 text-[10px] text-ink-muted">Groups templates by surgery type so they're easier to find.</div>
            </div>
            <label className="flex items-center gap-2 text-xs">
              <input type="checkbox" checked={tplShared} onChange={(e) => setTplShared(e.target.checked)} data-testid="ot-template-shared" />
              Share with other doctors / OT staff in this hospital
            </label>
            <div className="text-[11px] text-ink-muted">
              Saves the narrative fields (procedure, anaesthesia, consumables, intra/post-op notes, instructions, complications). Patient-specific data (IOL batch, signatures) is never saved into templates.
            </div>
            <Button onClick={saveAsTemplate} className="bg-brand hover:bg-brand-700" data-testid="ot-template-save">
              <Save size={14} className="mr-2" />Save template
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
